import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { Tabs } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { Overview } from './tabContent/Overview'
import { Events } from './tabContent/Events'
import { Initiatives } from './tabContent/Initiatives'
import { DevelopmentAreas } from './tabContent/DevelopmentAreas'
import { Concepts } from './tabContent/Concepts'
import useMediaQuery from '@app/util/useMediaQuery'
import { useParamState } from '@app/util/hooks/useParamState'
import { ServiceProviderViewModel } from 'functions/app/service-provider/view'
import {
  FEATURE_TOGGLE_IDENTIFIERS,
  FeatureToggleIdentifier,
} from '@schema/featureToggle'
import { isFeatureEnabled } from '@app/util/featureToggle'

import { Contact } from './Contact'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'

const tabs = {
  ...FEATURE_TOGGLE_IDENTIFIERS,
  contact: 'contact',
} as const

export const TabContent = ({
  serviceProvider,
}: {
  serviceProvider: ServiceProviderViewModel
}) => {
  const appTranslations = useTranslations().app
  const translations = appTranslations.serviceProviderPage
  const [tab, setTab] = useParamState(tabs, FEATURE_TOGGLE_IDENTIFIERS.overview)
  const isMobile = useMediaQuery()
  const paddingRight = isMobile ? '0rem' : '4.75rem'

  const isFeatureEnabledFor = (identifier: FeatureToggleIdentifier) =>
    isFeatureEnabled(serviceProvider.featureToggles, identifier)

  const showKnowledgeHubTab =
    (serviceProvider.knowledgeHubModules?.length ?? 0) > 0 &&
    isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.knowledgeHub)

  return (
    <Column
      alignItems="stretch"
      gap={isMobile ? '1rem' : '2rem'}
      style={{ paddingRight: paddingRight }}
    >
      <Tabs.Group selectedTab={tab}>
        {isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.overview) && (
          <Tabs.Tab
            label={translations.overview}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.overview}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.events) && (
          <Tabs.Tab
            label={translations.events}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.events}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.developmentArea) && (
          <Tabs.Tab
            label={translations.developmentArea}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.developmentArea}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.concepts) && (
          <Tabs.Tab
            label={translations.concepts}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.concepts}
            onSelectTab={setTab}
          />
        )}
        {isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.initiatives) && (
          <Tabs.Tab
            label={translations.initiatives}
            tabKey={FEATURE_TOGGLE_IDENTIFIERS.initiatives}
            onSelectTab={setTab}
          />
        )}
        {showKnowledgeHubTab && (
          <Tabs.Tab
            label={translations.knowledgeHub}
            tabKey={tabs.knowledgeHub}
            onSelectTab={setTab}
          />
        )}
        {isMobile && (
          <Tabs.Tab
            label={appTranslations.common.contact}
            tabKey={tabs.contact}
            onSelectTab={setTab}
          />
        )}
      </Tabs.Group>
      {match(tab)
        .with(FEATURE_TOGGLE_IDENTIFIERS.overview, () =>
          isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.overview) ? (
            <Overview
              key={FEATURE_TOGGLE_IDENTIFIERS.overview}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.events, () =>
          isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.events) ? (
            <Events
              key={FEATURE_TOGGLE_IDENTIFIERS.events}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.developmentArea, () =>
          isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.developmentArea) ? (
            <DevelopmentAreas
              key={FEATURE_TOGGLE_IDENTIFIERS.developmentArea}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.concepts, () =>
          isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.concepts) ? (
            <Concepts
              key={FEATURE_TOGGLE_IDENTIFIERS.concepts}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(FEATURE_TOGGLE_IDENTIFIERS.initiatives, () =>
          isFeatureEnabledFor(FEATURE_TOGGLE_IDENTIFIERS.initiatives) ? (
            <Initiatives
              key={FEATURE_TOGGLE_IDENTIFIERS.initiatives}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(tabs.contact, () => (
          <Contact key={tabs.contact} serviceProvider={serviceProvider} />
        ))
        .with(tabs.knowledgeHub, () =>
          showKnowledgeHubTab ? (
            <KnowledgeHubModulesSection
              key={tabs.knowledgeHub}
              modules={serviceProvider.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .exhaustive()}
    </Column>
  )
}
